import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { adminApi } from '../api';
import { resizeImageFile, isImageFile } from '../lib/imageResize';
import { Image as ImageIcon, Upload, Check, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const ChangeClientCoverDialog = ({ open, onOpenChange, client, onSaved }) => {
  const [tab, setTab] = useState('upload'); // upload | existing
  const [uploaded, setUploaded] = useState(null); // {dataUrl}
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [selected, setSelected] = useState(client?.cover_url || '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(client?.cover_url || '');
    setUploaded(null);
    // Load all existing photos of this client (across galleries/subgalleries)
    (async () => {
      try {
        const full = await adminApi.getClient(client.slug);
        const photos = [];
        for (const g of full.galleries) {
          const subs = await adminApi.listSubgalleries(g.id);
          for (const s of subs) {
            const detail = await adminApi.getSubgallery(s.id);
            (detail.photos || []).forEach(p => photos.push({ ...p, gallery_title: g.title, sub_title: s.title }));
          }
        }
        setExistingPhotos(photos);
      } catch (err) {
        console.error('ChangeClientCoverDialog: failed to load existing photos', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client]);

  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (f) => {
    if (!f) return;
    if (!isImageFile(f)) return toast.error('Deve essere un\'immagine');
    try {
      const { dataUrl } = await resizeImageFile(f, { maxSide: 1920, quality: 0.88 });
      setUploaded({ dataUrl });
      setSelected(dataUrl);
      setTab('upload');
    } catch { toast.error('Impossibile leggere l\'immagine'); }
  };

  const onUpload = (e) => { handleFile(e.target.files?.[0]); e.target.value=''; };

  const save = async () => {
    if (!selected) return toast.error('Scegli o carica una foto');
    setBusy(true);
    try {
      await adminApi.updateClient(client.id, { cover_url: selected });
      toast.success('Cover cliente aggiornata');
      onSaved && await onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
    finally { setBusy(false); }
  };

  const removeCover = async () => {
    if (!window.confirm('Rimuovere la foto di copertina personalizzata?')) return;
    setBusy(true);
    try {
      await adminApi.updateClient(client.id, { cover_url: '' });
      toast.success('Cover rimossa');
      onSaved && await onSaved();
      onOpenChange(false);
    } catch { toast.error('Errore'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><ImageIcon className="w-5 h-5 text-[#B8944B]" /> Foto di copertina</DialogTitle>
          <DialogDescription>Personalizza l'immagine di apertura dell'area di <b>{client?.name}</b> (mostrata al cliente quando accede).</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 border-b border-slate-200 mt-2">
          <button onClick={()=>setTab('upload')} className={`px-3 py-2 text-xs font-medium border-b-2 transition ${tab==='upload' ? 'border-[#B8944B] text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-900'}`}>Carica nuova</button>
          <button onClick={()=>setTab('existing')} className={`px-3 py-2 text-xs font-medium border-b-2 transition ${tab==='existing' ? 'border-[#B8944B] text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-900'}`}>Scegli da foto esistenti ({existingPhotos.length})</button>
        </div>

        {tab === 'upload' ? (
          <div className="mt-4 space-y-4">
            <label
              onDragOver={(e)=>{ e.preventDefault(); setDragOver(true); }}
              onDragLeave={()=>setDragOver(false)}
              onDrop={(e)=>{ e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
              className={`flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-8 cursor-pointer transition ${dragOver ? 'border-[#C9A961] bg-[#FAF5E6]/70' : 'border-slate-200 hover:border-[#C9A961] hover:bg-[#FAF5E6]/40'}`}>
              <Upload className="w-8 h-8 text-slate-400 mb-2" />
              <span className="text-sm text-slate-600">Clicca o trascina un'immagine</span>
              <span className="text-xs text-slate-400 mt-1">Ridimensionata automaticamente a max 1920px per un caricamento più veloce</span>
              <input type="file" accept="image/*,.heic,.heif" className="hidden" onChange={onUpload} />
            </label>
            {uploaded && (
              <div className="rounded-xl overflow-hidden bg-slate-100 aspect-[16/9]">
                <img src={uploaded.dataUrl} alt="preview" className="w-full h-full object-cover" />
              </div>
            )}
            {!uploaded && client?.cover_url && (
              <div>
                <p className="text-xs text-slate-500 mb-2">Cover attuale:</p>
                <div className="rounded-xl overflow-hidden bg-slate-100 aspect-[16/9]">
                  <img src={client.cover_url} alt="current" className="w-full h-full object-cover" />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-4">
            {existingPhotos.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">Nessuna foto disponibile. Carica un'immagine dalla scheda "Carica nuova".</p>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-[50vh] overflow-y-auto pr-1">
                {existingPhotos.map(p => (
                  <button key={p.id} type="button" onClick={()=>setSelected(p.url)} className={`relative aspect-square rounded-lg overflow-hidden bg-slate-100 border-2 transition ${selected === p.url ? 'border-[#C9A961]' : 'border-transparent hover:border-slate-300'}`}>
                    <img src={p.url} alt={p.filename} className="w-full h-full object-cover" />
                    {selected === p.url && <div className="absolute inset-0 bg-[#C9A961]/40 grid place-items-center"><div className="w-8 h-8 rounded-full bg-[#C9A961] text-black grid place-items-center"><Check className="w-4 h-4" /></div></div>}
                    <div className="absolute bottom-0 left-0 right-0 p-1 bg-gradient-to-t from-black/80 to-transparent text-white text-[9px] uppercase tracking-widest truncate">{p.sub_title}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-4 mt-2 border-t border-slate-100">
          <button type="button" disabled={busy || !client?.cover_url} onClick={removeCover} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-rose-500 disabled:opacity-30"><Trash2 className="w-3.5 h-3.5" /> Rimuovi cover</button>
          <div className="flex items-center gap-2">
            <button type="button" onClick={()=>onOpenChange(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Annulla</button>
            <button onClick={save} disabled={busy || !selected} className="inline-flex items-center gap-2 bg-black hover:bg-[#C9A961] hover:text-black text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {busy ? 'Salvataggio...' : 'Salva cover'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeClientCoverDialog;
